import { z } from 'zod';
import { contentIdSchema, pathSchema } from './content-id.js';
import { abilityNameSchema } from '../content/common.js';
/**
 * Бой: хиты и атаки (D4, D5).
 *
 * Хиты — не поле персонажа, а свёртка журнала, как и всё остальное:
 * получил урон, вылечился, отдохнул — шаги. Иначе текущее значение
 * пришлось бы хранить рядом с журналом и мирить их между собой,
 * а мирить два источника — это всегда однажды разойтись (D3).
 *
 * Атаки не хранятся вовсе: они выводятся из оружия в инвентаре,
 * владений и характеристик — теми же правилами, что и спасброски
 * (SRD 5.2, с. 22). Формулы лежат в записях оружия, а не в компоненте.
 */
export const hpStateSchema = z.strictObject({
    /** Текущие хиты: от нуля до максимума. */
    current: z.number().int().nonnegative(),
    /** Максимум — величина листа, посчитанная эффектами. */
    max: z.number().int().nonnegative(),
    /**
     * Временные хиты. Не складываются и хитами не являются
     * (с. 18): их тратят первыми, лечением не восстанавливают,
     * и до конца долгого отдыха они либо кончатся, либо исчезнут.
     */
    temp: z.number().int().nonnegative(),
    /** Кость хитов класса: «d10» у воина (с. 22). `null` — класса нет. */
    hitDie: z.string().min(1).nullable(),
    /** Сколько их: по одной за уровень (с. 22). */
    hitDice: z.number().int().nonnegative(),
    /**
     * Сколько потрачено в короткий отдых. Долгий возвращает все
     * (с. 185), поэтому число тоже свёртка, а не поле.
     */
    hitDiceSpent: z.number().int().nonnegative(),
});
/**
 * Атака оружием.
 *
 * Числа приходят величинами листа с провенансом: `attackPath` —
 * бонус атаки, `damagePath` — прибавка к урону. Кости урона остаются
 * записью («2d6»), потому что кости бросает роллер, а не движок
 * (D21.4).
 */
export const sheetAttackSchema = z.strictObject({
    id: contentIdSchema,
    name: z.string().min(1),
    /** Чем бьём: Сила, Ловкость либо лучшая из двух у фехтовального. */
    ability: abilityNameSchema,
    proficient: z.boolean(),
    attackPath: pathSchema,
    /**
     * Прибавка к урону; `null` — её нет вовсе. Так у заговоров:
     * модификатор характеристики к их урону не прибавляется, и рисовать
     * там ноль значило бы показать прибавку, которой нет.
     */
    damagePath: pathSchema.nullable(),
    damage: z.strictObject({
        /** Кости урона из записи оружия: «1d8». */
        notation: z.string().min(1),
        type: z.string().min(1),
        /** Урон двумя руками у универсального оружия; `null` — нет свойства. */
        versatile: z.string().min(1).nullable(),
    }),
    ranged: z.boolean(),
    /** Оружие в руках: надетое стоит первым, остальное — ниже. */
    equipped: z.boolean(),
    /** Атака заговором, а не оружием: кости из записи заклинания. */
    spell: z.boolean(),
});
export const combatSchema = z.strictObject({
    hp: hpStateSchema,
    attacks: z.array(sheetAttackSchema),
});
/**
 * Изменение хитов — команда (D3), а не правка поля на клиенте.
 * `temp` задаёт временные хиты целиком: они не складываются, и какие
 * оставить — решает игрок (с. 18).
 */
export const hpActionSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('damage'), amount: z.number().int().nonnegative() }),
    z.strictObject({ kind: z.literal('heal'), amount: z.number().int().nonnegative() }),
    z.strictObject({ kind: z.literal('temp'), amount: z.number().int().nonnegative() }),
    /**
     * Кость хитов в короткий отдых: «roll the die and add your
     * Constitution modifier … minimum of 1 Hit Point» (с. 187).
     *
     * `amount: null` — бросает сервер, и бросок ложится в журнал
     * бросков. Число — игрок бросил настоящую кость и внёс результат
     * (D12): оба пути равноправны, как и везде.
     */
    z.strictObject({
        kind: z.literal('spend_die'),
        amount: z.number().int().nullable(),
    }),
]);
export const hpProblemCodeSchema = z.enum([
    /** Отрицательный урон и лечение — разные вещи, и путать их нельзя. */
    'bad-amount',
    /** Лечить некого: хитов и так максимум. */
    'already-full',
    /** Хитов и так ноль: ниже нуля не бывает (с. 17). */
    'already-down',
    /** Костей хитов не осталось: все потрачены до отдыха. */
    'no-hit-dice',
    /** Класс не выбран, и кости хитов у персонажа нет. */
    'no-hit-die',
]);
export const hpProblemSchema = z.strictObject({
    code: hpProblemCodeSchema,
    message: z.string().min(1),
});
//# sourceMappingURL=combat.js.map